
import { defineComponent } from 'vue'
import { apiGet, apiPost } from "@/services/api/client";
import MarkdownIt from 'markdown-it'
import DOMPurify from 'dompurify'
import { User } from 'shared/User'
import { formatDateTime } from '@/utils/format'

type Entry = { role: 'user' | 'assistant'; text: string; html?: string }

const WIDGET_CONSTANTS = {

  MAX_HISTORY_LENGTH: 200,
  HISTORY_SLICE_LIMIT: 8,

  DEFAULT_WIDTH: 360,
  DEFAULT_HEIGHT: 520,

  POSITION_OFFSET_X: 400,
  POSITION_OFFSET_Y: 560,
  MARGIN: 16,
  
  MIN_WIDTH: 300,
  MIN_HEIGHT: 360,
  
  BUBBLE_MAX_WIDTH: '75%',
  BORDER_RADIUS: '12px',
  BORDER_RADIUS_SMALL: '8px',
  BORDER_RADIUS_BUBBLE: '10px',
  BORDER_RADIUS_TOGGLE: '999px'
} as const

export default defineComponent({
  name: 'StaffAssistantWidget',
  props: {
    ticketId: { type: String, required: false },
    model: { type: String, required: false, default: 'gemini-2.5-flash' }
  },
  data() {
    return {
      open: false,
      loading: false,
      message: '' as string,
      history: [] as Entry[],
      chats: [] as Array<{ id: string; title?: string; updatedAt: number; ticketId?: string | null }>,
      chatId: null as string | null,
      // Position & size
      posX: window.innerWidth - WIDGET_CONSTANTS.POSITION_OFFSET_X - WIDGET_CONSTANTS.MARGIN,
      posY: window.innerHeight - WIDGET_CONSTANTS.POSITION_OFFSET_Y - WIDGET_CONSTANTS.MARGIN,
      width: WIDGET_CONSTANTS.DEFAULT_WIDTH as number,
      height: WIDGET_CONSTANTS.DEFAULT_HEIGHT as number,
      dragging: false,
      resizing: false,
      dragStartX: 0,
      dragStartY: 0,
      startPosX: 0,
      startPosY: 0,
      startWidth: WIDGET_CONSTANTS.DEFAULT_WIDTH as number,
      startHeight: WIDGET_CONSTANTS.DEFAULT_HEIGHT as number,
      _cursor: undefined as number | undefined,
      _md: new MarkdownIt({ linkify: true, breaks: true }),
      _maxHistory: WIDGET_CONSTANTS.MAX_HISTORY_LENGTH,
      showChatList: false,
    }
  },
  computed: {
    modelName(): string { return this.model },
    currentChatTitle(): string { const c = this.chats.find(x => x.id === this.chatId); return c?.title || this.$t('panel.assistant.conversation'); },
    panelStyle(): Record<string, string> {
      return {
        left: this.posX + 'px',
        top: this.posY + 'px',
        width: this.width + 'px',
        height: this.height + 'px',
        position: 'fixed',
        zIndex: '1001'
      }
    },
    hasAssistantAccess(): boolean {
      // @ts-ignore
      const user = window.userObject as User;
      return user?.power >= 100
    }
  },
  mounted() {
    // Load last chat when opening widget
    this.$watch('open', async (v: boolean) => { if (v) await this.loadChats() })
    // Infinite scroll up for pagination
    this.$nextTick(() => {
      const container = this.$el.querySelector('.history') as HTMLElement | null
      if (!container) return
      container.addEventListener('scroll', async () => {
        if (container.scrollTop <= 0 && this._cursor) {
          await this.loadMessages(false)
        }
      })
    })
  },
  methods: {
    toggle() { 
      if (!this.hasAssistantAccess) return;
      this.open = !this.open 
    },
    toggleChatList() { 
      if (!this.hasAssistantAccess) return;
      this.showChatList = !this.showChatList 
    },
    openChat(c: { id: string }) { 
      if (!this.hasAssistantAccess) return;
      this.showChatList = false; 
      return this.switchChat(c) 
    },
    format(text: string) {
      try {
        const html = this._md.render(text || '')
        return DOMPurify.sanitize(html)
      } catch {
        return (text || '').replace(/\n/g, '<br/>')
      }
    },
    async copy(text: string) {
      try { await navigator.clipboard.writeText(text) } catch {}
    },
    formatTime(ts: number) { return formatDateTime(ts); },
    async loadChats() {
      if (!this.hasAssistantAccess) return;
      try {
        const list = await apiGet('/assistant/chats') as Array<{ id: string; title?: string; updatedAt: number; ticketId?: string | null }>
        this.chats = list
        this.chatId = this.chatId || list[0]?.id || null
        if (this.chatId) await this.loadMessages(true)
      } catch (e) {
        console.error("Failed to load chats:", e);
      }
    },
    async createChat() {
      if (!this.hasAssistantAccess) return;
      try {
        const created = await apiPost('/assistant/chats', { ticketId: this.ticketId }) as { id: string; title?: string; updatedAt: number }
        this.chatId = created.id
        await this.loadChats()
      } catch {}
    },
    async switchChat(c: { id: string }) {
      if (!this.hasAssistantAccess) return;
      if (this.chatId === c.id) return
      this.chatId = c.id
      this.history = []
      await this.loadMessages(true)
    },
    async loadMessages(reset: boolean) {
      if (!this.hasAssistantAccess) return;
      if (!this.chatId) return
      const container = this.$el.querySelector('.history') as HTMLElement | null
      const prevHeight = container?.scrollHeight || 0
      const cursor = reset ? undefined : (this._cursor as number | undefined)
      const result = await apiPost(`/assistant/chats/${this.chatId}/messages`, { cursor }) as { data: Entry[]; cursor?: number; hasMore: boolean }
      this._cursor = result.cursor
      // prepend for pagination or set on first load
      const mapped = result.data.map(m => ({ ...m, html: this.format(m.text) }))
      if (reset) {
        this.history = mapped
      } else {
        this.history = [...mapped, ...this.history]
      }
      if (this.history.length > this._maxHistory) this.history = this.history.slice(-this._maxHistory)
      this.$nextTick(() => {
        if (!container) return
        if (reset) {
          container.scrollTop = container.scrollHeight
        } else {
          container.scrollTop = container.scrollHeight - prevHeight
        }
      })
    },
    clampToViewport() {
      const maxX = Math.max(0, window.innerWidth - this.width - WIDGET_CONSTANTS.MARGIN)
      const maxY = Math.max(0, window.innerHeight - this.height - WIDGET_CONSTANTS.MARGIN)
      this.posX = Math.min(Math.max(WIDGET_CONSTANTS.MARGIN, this.posX), maxX)
      this.posY = Math.min(Math.max(WIDGET_CONSTANTS.MARGIN, this.posY), maxY)
    },
    startDrag(e: MouseEvent) {
      this.dragging = true
      this.dragStartX = e.clientX
      this.dragStartY = e.clientY
      this.startPosX = this.posX
      this.startPosY = this.posY
      window.addEventListener('mousemove', this.onDrag)
      window.addEventListener('mouseup', this.stopDrag)
    },
    onDrag(e: MouseEvent) {
      if (!this.dragging) return
      this.posX = this.startPosX + (e.clientX - this.dragStartX)
      this.posY = this.startPosY + (e.clientY - this.dragStartY)
      this.clampToViewport()
    },
    stopDrag() {
      this.dragging = false
      window.removeEventListener('mousemove', this.onDrag)
      window.removeEventListener('mouseup', this.stopDrag)
    },
    startResize(e: MouseEvent) {
      this.resizing = true
      this.dragStartX = e.clientX
      this.dragStartY = e.clientY
      this.startWidth = this.width
      this.startHeight = this.height
      window.addEventListener('mousemove', this.onResize)
      window.addEventListener('mouseup', this.stopResize)
    },
    onResize(e: MouseEvent) {
      if (!this.resizing) return
      const newW = this.startWidth + (e.clientX - this.dragStartX)
      const newH = this.startHeight + (e.clientY - this.dragStartY)
      this.width = Math.max(WIDGET_CONSTANTS.MIN_WIDTH, Math.min(newW, window.innerWidth - WIDGET_CONSTANTS.MARGIN))
      this.height = Math.max(WIDGET_CONSTANTS.MIN_HEIGHT, Math.min(newH, window.innerHeight - WIDGET_CONSTANTS.MARGIN))
      this.clampToViewport()
    },
    stopResize() {
      this.resizing = false
      window.removeEventListener('mousemove', this.onResize)
      window.removeEventListener('mouseup', this.stopResize)
    },
    async send() {
      if (!this.hasAssistantAccess) return;
      const msg = this.message.trim()
      if (!msg) return
      this.history.push({ role: 'user', text: msg, html: this.format(msg) })
      if (this.history.length > this._maxHistory) this.history = this.history.slice(-this._maxHistory)
      this.message = ''
      this.loading = true
      try {
        const res = await apiPost('/assistant/chat', {
          message: msg,
          ticketId: this.ticketId,
          history: this.history.slice(-WIDGET_CONSTANTS.HISTORY_SLICE_LIMIT),
          modelName: this.model,
          chatId: this.chatId,
        }) as { text: string; chatId?: string }
        if (!this.chatId && res.chatId) this.chatId = res.chatId
        this.history.push({ role: 'assistant', text: res.text, html: this.format(res.text) })
        if (this.history.length > this._maxHistory) this.history = this.history.slice(-this._maxHistory)
        await this.loadChats()
      } catch (e) {
        console.error("Failed to send message:", e);
        const err = 'Sorry, I ran into an error.'
        this.history.push({ role: 'assistant', text: err, html: this.format(err) })
      } finally {
        this.loading = false
        this.$nextTick(() => {
          const el = this.$el.querySelector('.history') as HTMLElement | null
          if (el) el.scrollTop = el.scrollHeight
        })
      }
    }
  }
})
